'use client';

/**
 * Shared "which of the holder's UGMs can this wallet actually protect"
 * computation, factored out of `DepositProtectionInfoDialog` so the
 * dialog and the manage-modal `ProtectionFooter` toggle compute the
 * EXACT same protectable set, budget, and V2.1-exclusion count.
 *
 * Why this exists: the footer was originally wired to enable across
 * EVERY group (`computeProtectionBudget(groups)`) with no `protectedUgms`,
 * which the server defaults to the full env-pinned allowlist. For a
 * MetaMask (`7715-grant`) holder whose allowlist includes a V2.1 board,
 * the onchain grant fired first and then `POST /api/protection/enable`
 * rejected it — leaving an orphaned permission and a toggle that snapped
 * back OFF. (The server now DROPS incompatible boards instead of
 * rejecting — see `filterProtectedUgmsForPath` — but the client still
 * filters V2.1 UGMs out of `protectedUgms` here so the grant caps are
 * sized to what's actually protectable.) Centralising that logic here
 * makes the two surfaces impossible to drift (per `budget.ts`).
 *
 * `signingPath` is passed in (rather than read from `useWalletCapabilities`
 * here) because the two callers resolve it slightly differently and
 * already hold the capability result.
 */

import { useMemo } from 'react';
import type { Address } from 'viem';
import type { CoinSeatsData } from '@/components/seats/coin-seats-card';
import {
  computeBudgetFromUgmSelection,
  groupSeatsByUgm,
  type ProtectionBudget,
  type ProtectionUgmRow,
} from './budget';
import { parseV21UgmSet } from '@/lib/protection/ugm-version';
import type { DelegationSigningPath } from '@/lib/wallet-capabilities';

export interface ProtectableUgms {
  /** Per-UGM aggregation of the holder's V2 seats (sorted heaviest-first). */
  ugmRows: ProtectionUgmRow[];
  /**
   * Lowercased UGM keys this wallet can sign protection for — every UGM
   * the holder has V2 tiles on, minus V2.1 managers when on the 7715
   * (MetaMask) path (which can't redeem against `msg.sender == holder`
   * boards). O(1) membership for budget recompute.
   */
  protectableUgmsLower: Set<string>;
  /** Checksummed, deterministically-ordered list for the wire payload. */
  protectableUgmsList: Address[];
  /**
   * Seat count on UGMs the wallet CAN'T protect (V2.1 on the 7715 path).
   * Zero on the 7710-direct path. Drives the "some boards excluded"
   * messaging so a disabled toggle isn't silent.
   */
  excludedV21Tiles: number;
  /**
   * Lowercased set of the configured V2.1 (7710-only) UGMs. Exposed so
   * the manage rail's per-board coverage tab can explain a 7715 holder
   * WHY an older board can't be covered, without re-parsing the env.
   */
  v21UgmsLower: Set<string>;
  /** Budget sized from the PROTECTABLE set only (same shape as the dialog). */
  budget: ProtectionBudget;
}

export function useProtectableUgms(args: {
  groups: CoinSeatsData[];
  signingPath: DelegationSigningPath;
  takeoverPriceUsd: number;
}): ProtectableUgms {
  const { groups, signingPath, takeoverPriceUsd } = args;

  const ugmRows = useMemo(() => groupSeatsByUgm(groups), [groups]);

  // V2.1 incompatibility set, parsed once. The literal `process.env.X`
  // access is load-bearing: Next.js only inlines `NEXT_PUBLIC_*` into
  // the client bundle when the property access is statically analysable
  // (see the long note in `use-deposit-protection.ts` `readEnv`).
  const v21Ugms = useMemo(
    () => parseV21UgmSet(process.env.NEXT_PUBLIC_PROTECTION_V21_UGMS),
    [],
  );

  const protectableUgmsLower = useMemo(() => {
    const lower = new Set<string>();
    for (const row of ugmRows) {
      const ugmKey = row.ugm.toLowerCase();
      if (signingPath === '7715-grant' && v21Ugms.has(ugmKey)) continue;
      lower.add(ugmKey);
    }
    return lower;
  }, [ugmRows, signingPath, v21Ugms]);

  const protectableUgmsList = useMemo(
    () =>
      ugmRows
        .filter((row) => protectableUgmsLower.has(row.ugm.toLowerCase()))
        .map((row) => row.ugm),
    [ugmRows, protectableUgmsLower],
  );

  const excludedV21Tiles = useMemo(() => {
    if (signingPath !== '7715-grant') return 0;
    let count = 0;
    for (const row of ugmRows) {
      if (v21Ugms.has(row.ugm.toLowerCase())) count += row.seatCount;
    }
    return count;
  }, [ugmRows, signingPath, v21Ugms]);

  const budget = useMemo(
    () => computeBudgetFromUgmSelection(ugmRows, protectableUgmsLower, takeoverPriceUsd),
    [ugmRows, protectableUgmsLower, takeoverPriceUsd],
  );

  return { ugmRows, protectableUgmsLower, protectableUgmsList, excludedV21Tiles, v21UgmsLower: v21Ugms, budget };
}
